import { enumArray, optionalBoolean, optionalString, stringArray } from '~/lib/search-params'
import type { Id } from '~convex/_generated/dataModel'

/** The two calendar layouts. Absent in the URL falls back to the persisted choice. */
export type CalendarView = 'month' | 'week'
export type CalendarScope = 'mine' | 'workspace'
const CALENDAR_AGENDA_KINDS = ['taskDue', 'siteVisit', 'appointment'] as const
export type CalendarAgendaKind = (typeof CALENDAR_AGENDA_KINDS)[number]

/**
 * The calendar page's URL state: the layout, the anchor date the visible
 * window is built around, and the same filter set the task board uses (minus
 * nothing — the chips are shared components). Unlike the tasks page there is
 * no default sprint narrowing: the calendar opens on the whole workspace.
 */
export type CalendarSearch = {
  /** Appointment selected by a deep link; the board owns its detail surface. */
  appointment?: Id<'appointments'>
  view?: CalendarView
  /** Any ISO `YYYY-MM-DD` inside the visible month/week; absent means today. */
  date?: string
  /** Absent preserves the workspace-wide calendar default. */
  scope?: CalendarScope
  projects?: Id<'projects'>[]
  assignees?: Id<'users'>[]
  kinds?: CalendarAgendaKind[]
  hideCompleted?: boolean
}

export const isCalendarView = (raw: unknown): raw is CalendarView =>
  raw === 'month' || raw === 'week'

export const isCalendarScope = (raw: unknown): raw is CalendarScope =>
  raw === 'mine' || raw === 'workspace'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const CONVEX_ID = /^[a-z0-9]{20,64}$/

const isConvexId = <T extends string>(raw: unknown): raw is T =>
  typeof raw === 'string' && CONVEX_ID.test(raw)

const convexIdArray = <T extends string>(raw: unknown): T[] | undefined =>
  stringArray<string>(raw)?.filter((value): value is T => isConvexId<T>(value))

/** Whitelisted decoder for the calendar search shape — drops unknowns silently. */
export const decodeCalendarSearch = (search: Record<string, unknown>): CalendarSearch => {
  const date = optionalString(search.date)
  const scope = isCalendarScope(search.scope) ? search.scope : undefined
  return {
    appointment: isConvexId<Id<'appointments'>>(search.appointment)
      ? search.appointment
      : undefined,
    // "Mine" already names the assignee at the server boundary. Treat it as
    // mutually exclusive with explicit people so a pasted mixed URL cannot
    // silently intersect the viewer with somebody else.
    assignees: scope === 'mine' ? undefined : convexIdArray<Id<'users'>>(search.assignees),
    date: date !== undefined && ISO_DATE.test(date) ? date : undefined,
    hideCompleted: optionalBoolean(search.hideCompleted),
    kinds: enumArray<CalendarAgendaKind>(search.kinds, CALENDAR_AGENDA_KINDS),
    projects: convexIdArray<Id<'projects'>>(search.projects),
    scope,
    view: isCalendarView(search.view) ? search.view : undefined,
  }
}
